import { Injectable } from '@angular/core';
import { Headers, Http, RequestOptions, Response } from '@angular/http';
import { Storage } from '@ionic/storage';
import AsyncLock from 'async-lock';
import { Observable } from 'rxjs';
import 'rxjs/add/operator/map';
import { Advert } from "../models/advert";
import { Chapter } from '../models/Chapter';
import { ContentBlock } from '../models/content-block';
import { Entry } from '../models/entry';
import { Subscription } from '../models/subscription';
import { User } from '../models/user';
import { NotFoundError } from './api/errors/NotFoundError';
import { PostLoginNotAcceptableError } from './api/errors/PostLoginNotAcceptableError';
import { UnauthorizedError } from './api/errors/UnauthorizedError';
import { UnprocessableEntityError } from './api/errors/UnprocessableEntityError';
import { PostActivateAndroidSubscriptionRequest } from './api/requests/PostActivateAndroidSubscriptionRequest';
import { PostApplyDiscountCodeRequest } from './api/requests/PostApplyDiscountCodeRequest';
import { PostContactRequest } from './api/requests/PostContactRequest';
import { PostLoginRequest } from './api/requests/PostLoginRequest';
import { PostRegisterRequest } from './api/requests/PostRegisterRequest';
import { PostSearchRequest } from './api/requests/PostSearchRequest';
import { ErrorResponse } from './api/responses/ErrorResponse';
import { GetAllChaptersResponse } from './api/responses/GetAllChaptersResponse';
import { GetChapterByIdResponse } from './api/responses/GetChapterByIdResponse';
import { GetEntryByIdResponse } from './api/responses/GetEntryByIdResponse';
import { GetHardcopyStatusResponse } from './api/responses/GetHardcopyStatusResponse';
import { GetLogoutResponse } from './api/responses/GetLogoutResponse';
import { GetProductIdsResponse } from './api/responses/GetProductIdsResponse';
import { GetSessionResponse } from './api/responses/GetSessionResponse';
import { PostActivateAndroidSubscriptionResponse } from './api/responses/PostActivateAndroidSubscriptionResponse';
import { PostApplyDiscountCodeResponse } from './api/responses/PostApplyDiscountCodeResponse';
import { PostContactResponse } from './api/responses/PostContactResponse';
import { PostLoginResponse } from './api/responses/PostLoginResponse';
import { PostRegisterResponse } from './api/responses/PostRegisterResponse';
import { PostSearchResponse, PostSearchResponseItem, PostSearchResponseItemModel, PostSearchResponseItemShortType } from './api/responses/PostSearchResponse';
import { PostActivateIosSubscriptionRequest } from './api/requests/PostActivateIosSubscriptionRequest';
import { PostActivateIosSubscriptionResponse } from './api/responses/PostActivateIosSubscriptionResponse';
import { PostHardcopyPurchaseRequest } from './api/requests/PostHardcopyPurchaseRequest';
import { PostHardcopyPurchaseResponse } from './api/responses/PostHardcopyPurchaseResponse';
import { PostChangePasswordRequest } from './api/requests/PostChangePasswordRequest';
import { PostChangePasswordResponse } from './api/responses/PostChangePasswordResponse';
import { ForbiddenError } from './api/errors/ForbiddenError';
import { GetHardcopyPurchaseUrlResponse } from "./api/responses/GetHardcopyPurchaseUrlResponse";
import { PostRegisterLicenseRequest } from "./api/requests/PostRegisterLicenseRequest";
import { PostRegisterLicenseResponse } from "./api/responses/PostRegisterLicenseResponse";
import { PostGeocodeRequest } from './api/requests/PostGeocodeRequest';
import { GeocodingResponse } from "@google/maps";
import { License } from "../models/License";

@Injectable()
export class Api {

    private endpoint: string = 'https://staging.ipadirectory.com/api/v1';
    private lock = new AsyncLock();
    private token: string;

    constructor(
        private http: Http,
        private storage: Storage,
    ) {

    }

    public clearToken() {
        this.setToken(null);
    }

    public clearTokenAndStore(): Promise<void> {
        this.clearToken();
        return this.lock.acquire('api_token', async () => {
            await this.storage.remove('api_token');
        });
    }

    public get(uri: string): Observable<any> {
        let options = this.getDefaultRequestOptions();
        let url: string = this.endpoint + uri;
        return this.http.get(url, options).map((res: Response) => res.json()).catch(err => this.handleError(err));
    }

    public getAllChapters(): Observable<GetAllChaptersResponse> {
        return this.get('/directory/chapters/all').map(result => {
            return {
                chapters: Chapter.createMany(result.chapters),
                advert: Advert.createOne(result.adverts),
            }
        });
    }

    public getChapterId(chapterId: number): Observable<GetChapterByIdResponse> {
        return this.get('/directory/chapters/' + chapterId + '/entries/all').map(result => {
            return {
                chapter: Chapter.createOne(result.chapter),
                entries: Entry.createMany(result.entries),
                advert: Advert.createOne(result.adverts),
            }
        });
    }

    public getEndpoint(): URL {
        return new URL(this.endpoint);
    }

    public getEntryById(entryId: number): Observable<GetEntryByIdResponse> {
        return this.get('/directory/entry/' + entryId + '/all').map(result => {
            return {
                content_blocks: ContentBlock.createMany(result.entry.content_blocks),
                entries: Entry.createMany(result.entry.entries),
                advert: Advert.createOne(result.adverts),
            }
        });
    }

    public getHardcopyStatus(): Observable<GetHardcopyStatusResponse> {
        return this.get('/hardcopy/status');
    }

    public getHardCopyPurchaseUrl(): Observable<GetHardcopyPurchaseUrlResponse> {
        return this.get('/hardcopy/purchase-url');
    }

    public getLogout(): Observable<GetLogoutResponse> {
        return this.get('/session/logout');
    }

    public getProductIds(): Observable<GetProductIdsResponse> {
        return this.get('/subscription/product-ids');
    }

    public getSession(): Observable<GetSessionResponse> {
        return this.get('/session').map(result => {
            return {
                subscription: Subscription.createOne(result.subscription),
                token: result.token,
                user: User.createOne(result.user),
                license: License.createOne(result.license),
            }
        });
    }

    public postActivateAndroidSubscription(request: PostActivateAndroidSubscriptionRequest): Observable<PostActivateAndroidSubscriptionResponse> {
        return this.post('/subscription/activate/android', request);
    }

    public postActivateIosSubscription(request: PostActivateIosSubscriptionRequest): Observable<PostActivateIosSubscriptionResponse> {
        return this.post('/subscription/activate/ios', request);
    }

    public postApplyDiscountCode(request: PostApplyDiscountCodeRequest): Observable<PostApplyDiscountCodeResponse> {
        return this.post('/subscription/apply-discount', request);
    }

    public postContact(request: PostContactRequest): Observable<PostContactResponse> {
        return this.post('/contact/general', request).map((response: PostContactResponse) => response);
    }

    public postChangePassword(request: PostChangePasswordRequest): Observable<PostChangePasswordResponse> {
        return this.post('/settings/password', request);
    }

    public postLogin(request: PostLoginRequest): Observable<PostLoginResponse> {
        return this.post('/session/login', request).catch(err => {
            if (err instanceof ErrorResponse) {
                if (err.code == 406) {
                    return Observable.throw(new PostLoginNotAcceptableError(err.error));
                }
            }
            return Observable.throw(err);
        }).map(response => {
            return {
                subscription: Subscription.createOne(response.subscription),
                token: response.token,
                user: User.createOne(response.user),
            }
        });
    }

    public postGeocode(request: PostGeocodeRequest): Observable<GeocodingResponse> {
        return this.post('/directory/geocode', request);
    }

    public postHardcopyPurchase(request: PostHardcopyPurchaseRequest): Observable<PostHardcopyPurchaseResponse> {
        return this.post('/hardcopy/purchase', request);
    }

    public postRegister(request: PostRegisterRequest): Observable<PostRegisterResponse> {
        return this.post('/session/register', request).map(response => {
            return {
                token: response.token,
                user: User.createOne(response.user),
            }
        });
    }

    public postRegisterLicense(request: PostRegisterLicenseRequest): Observable<PostRegisterLicenseResponse> {
        return this.post('/session/register-license', request);
    }

    public postSearch(request: PostSearchRequest): Observable<PostSearchResponseItem[]> {
        return this.post('/directory/search', request).map((response: PostSearchResponse) => {
            let results: PostSearchResponseItem[] = [];

            for (let key in response.items) {
                let item = response.items[key];
                let model: PostSearchResponseItemModel;
                let shortType: PostSearchResponseItemShortType;

                if (item.type == 'App\\Models\\Directory\\Chapter') {
                    shortType = 'Chapter';
                    model = Chapter.createOne(item.model);
                }
                if (item.type == 'App\\Models\\Directory\\ContentBlock') {
                    shortType = 'ContentBlock';
                    model = ContentBlock.createOne(item.model);
                }
                if (item.type == 'App\\Models\\Directory\\Entry') {
                    shortType = 'Entry';
                    model = Entry.createOne(item.model);
                }

                results.push({
                    model: model,
                    score: item.score,
                    short_type: shortType,
                    type: item.type,
                });
            }

            return results;
        });
    }

    public getDefaultHeaders(additional?: { [name: string] : any }) : Headers {
        let headers = new Headers({
            'Content-Type': 'application/json'
        });

        if (this.token) headers.append('Authorization', 'Bearer ' + this.token);

        if (additional) {
            for (let name in additional) {
                let value = additional[name];
                headers.append(name, value);
            }
        }

        return headers;
    }

    public getDefaultRequestOptions(): RequestOptions {
        let headers = this.getDefaultHeaders();
        let options = new RequestOptions({
            headers: headers
        });
        return options;
    }

    public handleError(err: Response) {
        if (err instanceof Response) {
            if (err.status == 401) return Observable.throw(new UnauthorizedError(err));
            if (err.status == 403) return Observable.throw(new ForbiddenError(err));
            if (err.status == 404) return Observable.throw(new NotFoundError(err));
            if (err.status == 422) return Observable.throw(new UnprocessableEntityError(err));
            return Observable.throw(new ErrorResponse(err));
        }
        return Observable.throw(err);
    }

    public post(uri: string, body?: any): Observable<any> {
        let options = this.getDefaultRequestOptions();
        let json: string = JSON.stringify(body);
        let url: string = this.endpoint + uri;
        return this.http.post(url, json, options).map((res: Response) => res.json()).catch(err => this.handleError(err));
    }

    public setToken(token: string) {
        this.token = token;
    }

    public errorsToUl(errors: { [k: string]: string[] }): HTMLUListElement {
        let ul = document.createElement('ul');

        for (let field in errors) {
            let items = errors[field];
            for (let item of items) {
                let li = document.createElement('li');
                li.innerText = item;
                ul.appendChild(li);
            }
        }

        return ul;
    }

}



// WEBPACK FOOTER //
// ./src/providers/api.ts